import { useCallback, useEffect, useState } from "react";
import {
  Article,
  Books,
  EnvelopeSimple,
  FolderUser,
  Gavel,
  House,
  Printer,
  Question,
  Receipt,
  Signature,
  Tray,
} from "@phosphor-icons/react";
import sidebarCollapsedLogo from "../../assets/images/sidebarCollapsedLogo.svg";
import { Layout, Menu, Popover } from "antd";
import {
  BottomWrapper,
  HorizondalLine,
  ImageContainer,
  ImgWrapper,
  LogoLockup,
  LogoWordmark,
  PrimarySideBarWrapper,
  ProfileIcon,
  SidebarBadge,
  StyledSidebarMenu,
} from "./PrimarySidebarStyled";
import { useNavigate, useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import NotificationDrawer from "../NotificationDrawer/NotificationDrawer";
import {
  MatterIcon,
  MatterIconActive,
  AIAssistantIcon,
  AIAssistantIconActive,
  NotificationBellActive,
  NotificationBellPassive,
  SettingsIcon,
  SettingsIconActive,
  VaESubmissionIcon,
  VaESubmissionIconActive,
} from "../../assets/icons";
import useAuth from "../../hooks/useAuth";
import { useDispatch, useSelector } from "react-redux";
import { fetchFeatureFlags } from "../../slice/FeatureFlagsSlice";
import { logout } from "../../slice/AuthSlice";
import { markSignedOut } from "../../utils/signedOutFlag";
import SearchModal from "../../pages/Search/SearchModal/SearchModal";
import { useUploadNotification } from "../../context/UploadNotificationContext";
import ProfilePopover, { getUserInitials } from "./ProfilePopover";
import {
  buildAIPaneSearch,
  buildClosedAIPaneSearch,
  isAIPaneOpen,
  pickDefaultAIPaneTab,
} from "../../utils/aiPaneUrl";
import {
  fetchInboxUnreadCount,
  selectInboxUnreadCount,
} from "../../slice/InboxSlice";
import {
  fetchFeeNoticeAvailability,
  selectFeeNoticeAvailable,
} from "../../slice/FeeNoticeSlice";
import {
  fetchIntakeReviewAvailability,
  selectIntakeReviewAvailable,
  selectIntakeReviewPendingCount,
} from "../../slice/IntakeReviewSlice";
const { Sider } = Layout;

// How often the sidebar refreshes the unread badge while the app is
// open (60s). Balances "users get a fresh count without refreshing"
// against "we don't pound the BE for a 1-int payload every poll
// tick." The Phase 5a read surface dispatches a one-shot refresh
// on mount; Phase 5b will additionally invalidate after every
// mark-reviewed / mark-unread / snooze mutation, so this interval
// is purely a safety net for cross-tab activity by another user.
const INBOX_UNREAD_REFRESH_INTERVAL_MS = 60 * 1000;

const PrimarySideBar = ({ collapsed }) => {
  const { isAdmin, user } = useAuth();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const logoutStatus = useSelector((state) => state.auth?.logoutStatus);
  const [notificationDrawerOpen, setNotificationDrawerOpen] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [openPopoverKey, setOpenPopoverKey] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const isMatterDetailPage = /^\/matter\/[^/]+/.test(location.pathname);
  const { showNotification: showUploadNotification } = useUploadNotification();

  // Inbox unread badge — server-authoritative count of inbox-eligible
  // documents the user hasn't explicitly marked reviewed. Snoozed
  // items are currently included per the BE contract (FE has flagged
  // this as a UX question — see service/inbox.js comment).
  const inboxUnreadCount = useSelector(selectInboxUnreadCount);

  // Fee Notice nav entry (finance queue) only appears once the backend
  // routes are actually deployed — per the frontend handoff, "hide nav
  // entry when the list endpoint 404s/errors" rather than gating on a
  // dedicated feature flag.
  const feeNoticeAvailable = useSelector(selectFeeNoticeAvailable);

  // Intake review nav entry — same posture as the finance queue: the icon
  // appears only once the routes answer and the user is on the reviewing
  // attorneys' allowlist, so nobody sees a door they cannot open.
  const intakeReviewAvailable = useSelector(selectIntakeReviewAvailable);
  const intakeReviewPendingCount = useSelector(selectIntakeReviewPendingCount);

  const handleLogout = async () => {
    // Intentionally leave the popover open across the dispatch so the
    // "Signing out…" loading state is actually visible. The hard redirect
    // below unmounts everything, so we don't need to close it ourselves.
    try {
      await dispatch(logout()).unwrap();
    } catch (error) {
      console.error("Logout failed:", error);
    }
    // Without this flag, AuthRedirect auto-launches the Salesforce OAuth
    // flow on landing, and an alive Salesforce session silently signs the
    // user straight back in — sign out looked like a mere page refresh.
    // The flag makes the next landing show the explicit sign-in page.
    markSignedOut();
    // Hard redirect to "/" so ProtectedRoute + AuthRedirect re-run the login
    // flow against the freshly-cleared cookies.
    globalThis.location.href = "/";
  };

  // Determine page key from pathname. Inbox is checked before
  // mail-intake because the literal substring "inbox" is unique
  // (mail-intake doesn't contain it), but ordering matters for
  // stability: any future path that contains "inbox" as a substring
  // would otherwise leak into mail-intake or matter cases.
  const getPageKey = useCallback(() => {
    if (location.pathname.includes("notifications")) return "4";
    if (location.pathname.includes("home")) return "1";
    if (location.pathname.startsWith("/brief-bank")) return "9";
    if (location.pathname.startsWith("/articles-bank")) return "15";
    if (location.pathname.startsWith("/my-files")) return "11";
    if (location.pathname.startsWith("/inbox")) return "7";
    if (location.pathname.startsWith("/fax")) return "8";
    if (location.pathname.includes("mail-intake")) return "6";
    if (location.pathname.startsWith("/fee-notices")) return "10";
    if (location.pathname.startsWith("/intake-review")) return "14";
    if (location.pathname.startsWith("/va-submissions")) return "13";
    if (location.pathname.startsWith("/signatures")) return "16";
    if (location.pathname.startsWith("/help")) return "12";
    if (location.pathname.includes("matter")) return "3";
    if (location.pathname.includes("settings")) return "5";
    return "3";
  }, [location.pathname]);

  const isNotificationPage = location.pathname.includes("notifications");

  const selectedKey = notificationDrawerOpen ? "4" : getPageKey();

  const handleNavigate = ({ key, path }) => {
    setOpenPopoverKey(null);
    setNotificationDrawerOpen(false);
    
    // If we're on Matter Details page, check for upload notification
    if (isMatterDetailPage) {
      // If upload notification is showing, use regular navigation to avoid interrupting uploads
      // Otherwise, force full page refresh to clear WebViewer WASM memory
      if (showUploadNotification) {
        console.log('📤 [NAVIGATION] Upload notification visible - using regular navigation');
        navigate(path);
      } else {
        console.log('🔄 [NAVIGATION] No upload notification - using full page refresh');
        window.location.href = path;
      }
    } else {
      navigate(path);
    }
  };

  const handleNotificationClick = () => {
    setOpenPopoverKey(null);
    if (isNotificationPage) return;
    setNotificationDrawerOpen((prev) => !prev);
  };

  const handleAIPaneToggle = () => {
    setOpenPopoverKey(null);
    if (!isMatterDetailPage) return;
    const isOpen = isAIPaneOpen(location.search);

    if (isOpen) {
      // Close: strip both `?ai=` and the legacy `?chatbox=true`.
      const search = buildClosedAIPaneSearch(location.search);
      navigate(`${location.pathname}${search}`, { replace: true });
      return;
    }

    // Open: pick the smart default — Briefing if a doc is in focus, Chat
    // when the user is at the matter root (Briefing has nothing to render
    // without a `documentId`).
    //
    // Note: priming the chatBot welcome message lives in `AIPane`'s
    // active-tab effect (single source of truth for both user-switch and
    // refresh/deep-link paths). Doing it here too would race the effect
    // and potentially fire two welcome thunks for the same matter.
    const defaultTab = pickDefaultAIPaneTab(location.pathname);
    const search = buildAIPaneSearch(location.search, defaultTab);
    navigate(`${location.pathname}${search}`, { replace: true });
  };

  useEffect(() => {
    dispatch(fetchFeatureFlags());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchFeeNoticeAvailability());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchIntakeReviewAvailability());
  }, [dispatch]);

  // Inbox unread badge: one-shot refresh on mount + periodic refresh
  // while the app is open. The interval pauses naturally when the
  // window is backgrounded (browsers throttle setInterval in hidden
  // tabs to ≥1min already); we don't add a visibilitychange listener
  // here because the cost of an occasional tick on a hidden tab is
  // a single int payload, while the complexity of de-throttling on
  // visible re-entry would exceed that budget.
  useEffect(() => {
    dispatch(fetchInboxUnreadCount());
    const intervalId = setInterval(() => {
      dispatch(fetchInboxUnreadCount());
    }, INBOX_UNREAD_REFRESH_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [dispatch]);

  useEffect(() => {
    setOpenPopoverKey(null);
  }, [location.pathname]);

  return (
    <PrimarySideBarWrapper>
      <Sider trigger={null} collapsible collapsed={collapsed}>
        <div className="demo-logo-vertical" />
        <ImgWrapper>
          <ImageContainer>
            <LogoLockup>
              <img src={sidebarCollapsedLogo} alt="logo" />
              <LogoWordmark>Docs</LogoWordmark>
            </LogoLockup>
            <HorizondalLine />
          </ImageContainer>
        </ImgWrapper>
        <StyledSidebarMenu>
          <Menu
            mode="inline"
            selectedKeys={[selectedKey]}
            inlineCollapsed={collapsed}
            items={[
              {
                key: "1",
                icon: (
                  <Popover
                    placement="right"
                    content="Home"
                    open={openPopoverKey === "1"}
                    onOpenChange={(open) => setOpenPopoverKey(open ? "1" : null)}
                  >
                    <span style={{ display: "inline-flex" }}>
                      <House
                        size={24}
                        weight={selectedKey === "1" ? "fill" : "regular"}
                        color={selectedKey === "1" ? "#0C53BB" : "#98A6BA"}
                      />
                    </span>
                  </Popover>
                ),
                onClick: () => handleNavigate({ key: "1", path: "/home" }),
              },
              // {
              //   key: "2",
              //   icon: <MagnifyingGlass size={24} color="#98A6BA" />,
              //   onClick: () => {
              //     console.log("Search modal to appear");
              //   },
              // },
              {
                key: "3",
                icon: (
                  <Popover
                    placement="right"
                    content="Matter"
                    open={openPopoverKey === "3"}
                    onOpenChange={(open) => setOpenPopoverKey(open ? "3" : null)}
                  >
                    <span style={{ display: "inline-flex" }}>
                      {selectedKey === "3" ? (
                        <MatterIconActive />
                      ) : (
                        <MatterIcon />
                      )}
                    </span>
                  </Popover>
                ),
                onClick: () => handleNavigate({ key: "3", path: "/matter" }),
              },
              {
                key: "9",
                icon: (
                  <Popover
                    placement="right"
                    content="Brief Bank"
                    open={openPopoverKey === "9"}
                    onOpenChange={(open) => setOpenPopoverKey(open ? "9" : null)}
                  >
                    <span style={{ display: "inline-flex" }}>
                      <Books
                        size={24}
                        weight={selectedKey === "9" ? "fill" : "regular"}
                        color={selectedKey === "9" ? "#0C53BB" : "#98A6BA"}
                      />
                    </span>
                  </Popover>
                ),
                onClick: () =>
                  handleNavigate({ key: "9", path: "/brief-bank" }),
              },
              {
                key: "15",
                icon: (
                  <Popover
                    placement="right"
                    content="Articles Bank"
                    open={openPopoverKey === "15"}
                    onOpenChange={(open) => setOpenPopoverKey(open ? "15" : null)}
                  >
                    <span style={{ display: "inline-flex" }}>
                      <Article
                        size={24}
                        weight={selectedKey === "15" ? "fill" : "regular"}
                        color={selectedKey === "15" ? "#0C53BB" : "#98A6BA"}
                      />
                    </span>
                  </Popover>
                ),
                onClick: () =>
                  handleNavigate({ key: "15", path: "/articles-bank" }),
              },
              {
                key: "11",
                icon: (
                  <Popover
                    placement="right"
                    content="My Files"
                    open={openPopoverKey === "11"}
                    onOpenChange={(open) => setOpenPopoverKey(open ? "11" : null)}
                  >
                    <span style={{ display: "inline-flex" }}>
                      <FolderUser
                        size={24}
                        weight={selectedKey === "11" ? "fill" : "regular"}
                        color={selectedKey === "11" ? "#0C53BB" : "#98A6BA"}
                      />
                    </span>
                  </Popover>
                ),
                onClick: () => handleNavigate({ key: "11", path: "/my-files" }),
              },
              {
                key: "6",
                icon: (
                  <Popover
                    placement="right"
                    content="Mail Intake"
                    open={openPopoverKey === "6"}
                    onOpenChange={(open) => setOpenPopoverKey(open ? "6" : null)}
                  >
                    <span style={{ display: "inline-flex" }}>
                      <Tray
                        size={24}
                        weight={selectedKey === "6" ? "fill" : "regular"}
                        color={selectedKey === "6" ? "#0C53BB" : "#98A6BA"}
                      />
                    </span>
                  </Popover>
                ),
                onClick: () => handleNavigate({ key: "6", path: "/mail-intake" }),
              },
              ...(feeNoticeAvailable
                ? [
                    {
                      key: "10",
                      icon: (
                        <Popover
                          placement="right"
                          content="Fee Notices"
                          open={openPopoverKey === "10"}
                          onOpenChange={(open) =>
                            setOpenPopoverKey(open ? "10" : null)
                          }
                        >
                          <span style={{ display: "inline-flex" }}>
                            <Receipt
                              size={24}
                              weight={selectedKey === "10" ? "fill" : "regular"}
                              color={selectedKey === "10" ? "#0C53BB" : "#98A6BA"}
                            />
                          </span>
                        </Popover>
                      ),
                      onClick: () =>
                        handleNavigate({ key: "10", path: "/fee-notices" }),
                    },
                  ]
                : []),
              ...(intakeReviewAvailable
                ? [
                    {
                      key: "14",
                      icon: (
                        <Popover
                          placement="right"
                          content={
                            intakeReviewPendingCount > 0
                              ? `Intake review · ${intakeReviewPendingCount} to decide`
                              : "Intake review"
                          }
                          open={openPopoverKey === "14"}
                          onOpenChange={(open) =>
                            setOpenPopoverKey(open ? "14" : null)
                          }
                        >
                          <span style={{ display: "inline-flex" }}>
                            <Gavel
                              size={24}
                              weight={selectedKey === "14" ? "fill" : "regular"}
                              color={selectedKey === "14" ? "#0C53BB" : "#98A6BA"}
                            />
                          </span>
                        </Popover>
                      ),
                      onClick: () =>
                        handleNavigate({ key: "14", path: "/intake-review" }),
                    },
                  ]
                : []),
              {
                key: "7",
                icon: (
                  <Popover
                    placement="right"
                    content={
                      inboxUnreadCount > 0
                        ? `Inbox · ${inboxUnreadCount} unread`
                        : "Inbox"
                    }
                    open={openPopoverKey === "7"}
                    onOpenChange={(open) => setOpenPopoverKey(open ? "7" : null)}
                  >
                    <span
                      style={{
                        display: "inline-flex",
                        position: "relative",
                      }}
                    >
                      <EnvelopeSimple
                        size={24}
                        weight={selectedKey === "7" ? "fill" : "regular"}
                        color={selectedKey === "7" ? "#0C53BB" : "#98A6BA"}
                      />
                      {inboxUnreadCount > 0 && (
                        <SidebarBadge
                          aria-label={`${inboxUnreadCount} unread inbox item${
                            inboxUnreadCount === 1 ? "" : "s"
                          }`}
                        >
                          {inboxUnreadCount > 99 ? "99+" : inboxUnreadCount}
                        </SidebarBadge>
                      )}
                    </span>
                  </Popover>
                ),
                onClick: () => handleNavigate({ key: "7", path: "/inbox" }),
              },
              {
                // VA E-Submission: moved off the Home tab bar
                // (2026-08-28, "free up this screen to be a real work
                // tool") — it's a submission console you visit, not
                // part of the daily stack.
                key: "13",
                icon: (
                  <Popover
                    placement="right"
                    content="VA E-Submission"
                    open={openPopoverKey === "13"}
                    onOpenChange={(open) => setOpenPopoverKey(open ? "13" : null)}
                  >
                    <span style={{ display: "inline-flex" }}>
                      {selectedKey === "13" ? (
                        <VaESubmissionIconActive />
                      ) : (
                        <VaESubmissionIcon />
                      )}
                    </span>
                  </Popover>
                ),
                onClick: () => handleNavigate({ key: "13", path: "/va-submissions" }),
              },
              {
                // E-Signatures: what I sent for signature, who it is
                // pending with, chase or cancel it (Matthew, 2026-10-06:
                // "an icon in the lefthand sidebar with the signature
                // info similar to the VA e-submission page"). Needs-my-
                // signature rows also live in My Work; the sent list
                // lives only here.
                key: "16",
                icon: (
                  <Popover
                    placement="right"
                    content="E-Signatures"
                    open={openPopoverKey === "16"}
                    onOpenChange={(open) => setOpenPopoverKey(open ? "16" : null)}
                  >
                    <span style={{ display: "inline-flex" }} data-testid="sidebar-signatures-icon">
                      <Signature
                        size={24}
                        weight={selectedKey === "16" ? "fill" : "regular"}
                        color={selectedKey === "16" ? "#0C53BB" : "#98A6BA"}
                      />
                    </span>
                  </Popover>
                ),
                onClick: () => handleNavigate({ key: "16", path: "/signatures" }),
              },
              {
                key: "8",
                icon: (
                  <Popover
                    placement="right"
                    content="Fax"
                    open={openPopoverKey === "8"}
                    onOpenChange={(open) => setOpenPopoverKey(open ? "8" : null)}
                  >
                    <span style={{ display: "inline-flex" }}>
                      <Printer
                        size={24}
                        weight={selectedKey === "8" ? "fill" : "regular"}
                        color={selectedKey === "8" ? "#0C53BB" : "#98A6BA"}
                      />
                    </span>
                  </Popover>
                ),
                onClick: () => handleNavigate({ key: "8", path: "/fax" }),
              },
              ...(isMatterDetailPage
                ? [
                    {
                      key: "chatbox",
                      icon: (
                        <Popover
                          placement="right"
                          content="AI Assistant"
                          open={openPopoverKey === "chatbox"}
                          onOpenChange={(open) =>
                            setOpenPopoverKey(open ? "chatbox" : null)
                          }
                        >
                          <span style={{ display: "inline-flex" }}>
                            {isAIPaneOpen(location.search) ? (
                              <AIAssistantIconActive />
                            ) : (
                              <AIAssistantIcon />
                            )}
                          </span>
                        </Popover>
                      ),
                      onClick: handleAIPaneToggle,
                    },
                  ]
                : []),
            ]}
          />
        </StyledSidebarMenu>
      </Sider>

      <BottomWrapper>
        <Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          style={{ flex: 0 }}
        >
          <StyledSidebarMenu>
            <Menu
              mode="inline"
              selectedKeys={[selectedKey]}
              inlineCollapsed={collapsed}
              items={[
                {
                  key: "12",
                  icon: (
                    <Popover
                      placement="right"
                      content="Help"
                      open={openPopoverKey === "12"}
                      onOpenChange={(open) =>
                        setOpenPopoverKey(open ? "12" : null)
                      }
                    >
                      <span style={{ display: "inline-flex" }}>
                        <Question
                          size={24}
                          weight={selectedKey === "12" ? "fill" : "regular"}
                          color={selectedKey === "12" ? "#0C53BB" : "#98A6BA"}
                        />
                      </span>
                    </Popover>
                  ),
                  onClick: () => handleNavigate({ key: "12", path: "/help" }),
                },
                ...(isAdmin
                  ? [
                      {
                        key: "5",
                        className: "custom-settings-item",
                        icon: (
                          <Popover
                            placement="right"
                            content="Settings"
                            open={openPopoverKey === "5"}
                            onOpenChange={(open) =>
                              setOpenPopoverKey(open ? "5" : null)
                            }
                          >
                            <span style={{ display: "inline-flex" }}>
                              {selectedKey === "5" ? (
                                <SettingsIconActive />
                              ) : (
                                <SettingsIcon />
                              )}
                            </span>
                          </Popover>
                        ),
                        onClick: () =>
                          handleNavigate({ key: "5", path: "/settings" }),
                      },
                    ]
                  : []),
                {
                  key: "4",
                  icon: (
                    <Popover
                      placement="right"
                      content="Notifications"
                      open={openPopoverKey === "4"}
                      onOpenChange={(open) =>
                        setOpenPopoverKey(open ? "4" : null)
                      }
                    >
                      <span style={{ display: "inline-flex" }}>
                        {selectedKey === "4" ? (
                          <NotificationBellActive />
                        ) : (
                          <NotificationBellPassive />
                        )}
                      </span>
                    </Popover>
                  ),
                  onClick: handleNotificationClick,
                },
              ]}
            />
          </StyledSidebarMenu>
        </Sider>
        <Popover
          placement="rightBottom"
          trigger="click"
          open={profileOpen}
          onOpenChange={setProfileOpen}
          content={
            <ProfilePopover
              user={user}
              loading={logoutStatus === "loading"}
              onLogout={handleLogout}
            />
          }
          overlayInnerStyle={{ padding: 12, borderRadius: 8 }}
        >
          <ProfileIcon
            role="button"
            tabIndex={0}
            aria-label="Open user menu"
            aria-haspopup="menu"
            aria-expanded={profileOpen}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setProfileOpen((prev) => !prev);
              }
            }}
          >
            {getUserInitials(user)}
          </ProfileIcon>
        </Popover>

        <NotificationDrawer
          open={notificationDrawerOpen}
          setOpen={setNotificationDrawerOpen}
        />
        <SearchModal
          open={showSearchModal}
          onClose={() => setShowSearchModal(false)}
        />
      </BottomWrapper>
    </PrimarySideBarWrapper>
  );
};

export default PrimarySideBar;
PrimarySideBar.propTypes = {
  collapsed: PropTypes.bool.isRequired,
};
